import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Loader2, Zap, Check, Lock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLASSES = ['1', '2', '3', '4', '5', '6', '7', '8'];
const FEATURES = ['Alertes email dès qu\'un marché correspond à vos métiers + régions', 'Matching automatique par classe d\'agréation', 'Digest hebdomadaire par email', 'Filtres sauvegardés'];

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#1E3A8A]'}`}>{children}</button>
);

// Subscription CTA (non-subscribers) or saved-filters / alerts panel (subscribers)
export const TenderAlertsPanel = ({ meta, onChanged }) => {
  const { user, refreshUser } = useAuth();
  const [prefs, setPrefs] = useState({ enabled: true, trades: [], regions: [], accreditation_classes: [], weekly_digest: true });
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(null);
  const [lite, setLite] = useState({ email: '', password: '', name: '' });
  const [registering, setRegistering] = useState(false);

  useEffect(() => { if (meta?.alerts && Object.keys(meta.alerts).length) setPrefs((p) => ({ ...p, ...meta.alerts })); }, [meta]);

  const toggle = (key, value) => setPrefs((p) => ({ ...p, [key]: p[key].includes(value) ? p[key].filter((v) => v !== value) : [...p[key], value] }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tenders/alerts`, prefs, { withCredentials: true });
      toast.success('Filtres et alertes enregistrés');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const checkout = async (planId) => {
    setPaying(planId);
    try {
      const res = await axios.post(`${API}/tenders/checkout`, { plan_id: planId, origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la création du paiement'); setPaying(null); }
  };

  const registerLite = async (e) => {
    e.preventDefault();
    setRegistering(true);
    try {
      await axios.post(`${API}/tenders/register-lite`, lite, { withCredentials: true });
      await refreshUser?.();
      toast.success('Compte créé — choisissez votre formule ci-dessous');
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la création du compte'); }
    finally { setRegistering(false); }
  };

  if (meta?.access) {
    return (
      <Card className="border-[#1E3A8A]/20" data-testid="tender-alerts-panel">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Bell className="w-5 h-5 text-[#F59E0B]" /> Mes alertes marchés publics</h3>
              <p className="text-sm text-[#6B7280]">
                {meta.access_source === 'premium' ? 'Inclus dans votre abonnement Premium.' : `Abonnement actif jusqu'au ${meta.tenders_expiry ? new Date(meta.tenders_expiry).toLocaleDateString('fr-FR') : ''}.`} Vous recevez un email dès qu'un nouveau marché correspond à vos filtres.
              </p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700" data-testid="tender-access-badge">Actif</Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-md">
            <div><p className="font-medium text-sm">Alertes email instantanées</p><p className="text-xs text-[#6B7280]">À chaque nouveau marché correspondant</p></div>
            <Switch checked={prefs.enabled} onCheckedChange={(v) => setPrefs({ ...prefs, enabled: v })} data-testid="alerts-enabled-switch" />
          </div>
          <div className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-md">
            <div><p className="font-medium text-sm">Digest hebdomadaire</p><p className="text-xs text-[#6B7280]">Chaque lundi, le récapitulatif de la semaine</p></div>
            <Switch checked={prefs.weekly_digest} onCheckedChange={(v) => setPrefs({ ...prefs, weekly_digest: v })} data-testid="alerts-digest-switch" />
          </div>
          <div>
            <Label className="text-sm">Métiers suivis <span className="text-[#9CA3AF] font-normal">(aucun = tous)</span></Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="alerts-trades">
              {(meta.trades || []).map((t) => <Chip key={t.id} active={prefs.trades.includes(t.id)} onClick={() => toggle('trades', t.id)} testId={`alert-trade-${t.id}`}>{t.label}</Chip>)}
            </div>
          </div>
          <div>
            <Label className="text-sm">Provinces suivies <span className="text-[#9CA3AF] font-normal">(aucune = toute la Belgique)</span></Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="alerts-regions">
              {(meta.regions || []).map((r) => <Chip key={r.id} active={prefs.regions.includes(r.id)} onClick={() => toggle('regions', r.id)} testId={`alert-region-${r.id}`}>{r.label}</Chip>)}
            </div>
          </div>
          <div>
            <Label className="text-sm">Classes d'agréation détenues <span className="text-[#9CA3AF] font-normal">(matching automatique — aucune = pas de filtre)</span></Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="alerts-classes">
              {CLASSES.map((c) => <Chip key={c} active={prefs.accreditation_classes.includes(c)} onClick={() => toggle('accreditation_classes', c)} testId={`alert-class-${c}`}>Classe {c}</Chip>)}
            </div>
          </div>
          <Button className="w-full btn-primary" onClick={save} disabled={saving} data-testid="save-alerts-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Enregistrer mes filtres et alertes
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#F59E0B]/40 bg-gradient-to-br from-white to-[#FFF7ED]" data-testid="tender-subscribe-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1"><Zap className="w-5 h-5 text-[#F59E0B]" /><h3 className="font-heading font-bold text-lg">Alertes Marchés Publics</h3></div>
        <p className="text-sm text-[#4B5563] mb-4">Ne ratez plus aucun appel d'offres : recevez les marchés de vos métiers et provinces directement par email.</p>
        <ul className="space-y-2 mb-5">
          {FEATURES.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-[#374151]"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />{f}</li>)}
        </ul>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {(meta?.plans || []).map((p) => (
            <div key={p.id} className={`rounded-lg border p-3 text-center ${p.id === 'tenders_yearly' ? 'border-[#F59E0B] bg-white' : 'border-[#E5E7EB] bg-white'}`} data-testid={`tender-plan-${p.id}`}>
              <p className="text-2xl font-bold text-[#111827]">{p.amount.toFixed(2).replace('.', ',')} €</p>
              <p className="text-xs text-[#6B7280]">HTVA / {p.duration_months === 12 ? 'an' : 'mois'}{p.id === 'tenders_yearly' && <span className="block text-[#F59E0B] font-semibold">2 mois offerts</span>}</p>
              {user ? (
                <Button size="sm" className="mt-2 w-full btn-accent" onClick={() => checkout(p.id)} disabled={!!paying} data-testid={`tender-checkout-${p.id}`}>
                  {paying === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Souscrire'}
                </Button>
              ) : null}
            </div>
          ))}
        </div>
        {!user ? (
          <form onSubmit={registerLite} className="space-y-2 border-t border-[#E5E7EB] pt-4" data-testid="lite-register-form">
            <p className="text-xs text-[#6B7280] flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Aucun compte BuildConnect classique requis : un email et un mot de passe suffisent.</p>
            <Input type="email" required placeholder="Votre email" value={lite.email} onChange={(e) => setLite({ ...lite, email: e.target.value })} className="input-field" data-testid="lite-email" />
            <Input type="password" required minLength={6} placeholder="Mot de passe (6 caractères min.)" value={lite.password} onChange={(e) => setLite({ ...lite, password: e.target.value })} className="input-field" data-testid="lite-password" />
            <Input placeholder="Nom / société (optionnel)" value={lite.name} onChange={(e) => setLite({ ...lite, name: e.target.value })} className="input-field" data-testid="lite-name" />
            <Button type="submit" className="w-full btn-primary" disabled={registering} data-testid="lite-register-btn">
              {registering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Créer mon accès et choisir une formule
            </Button>
            <p className="text-xs text-center text-[#6B7280]">Déjà membre ? <a href="/login?redirect=/marches-publics" className="text-[#1E3A8A] font-medium">Connectez-vous</a> — inclus gratuitement pour les abonnés Premium.</p>
          </form>
        ) : (
          <p className="text-xs text-[#6B7280] text-center">Inclus automatiquement dans l'abonnement <a href="/dashboard/subscription" className="text-[#1E3A8A] font-medium">Premium</a>. Prix HTVA, TVA calculée au paiement.</p>
        )}
      </CardContent>
    </Card>
  );
};
